import { Link, useNavigate } from "react-router-dom";
import { ActionIcon, Button, Center, Container, Divider, Group, Loader, Menu, Paper, ScrollArea, SimpleGrid, Space, Stack, Text, ThemeIcon, Title, useMantineTheme } from "@mantine/core"
import { useMediaQuery } from "@mantine/hooks";
import { IconChevronLeft, IconDots, IconPencil, IconTag, IconTrash } from "@tabler/icons-react";
import moment from "moment";

import { useAccess } from "../../Auth/auth-functions";
import { AzureADRoles } from "../../Auth/auth-roles";
import { CategoryDto } from "../../models/categories.models";
import { CategoryWorkOrderList } from "./CategoryWorkOrderList";
import { ListWorkOrderDto } from "../../models/workorder.models";
import { CrudUserDate } from "../Shared/CrudUserDate";
import classes from './CategoryDetailView.module.css';

interface CategoryDetailViewProps {
	category?: CategoryDto;
	isLoading: boolean;
	onEdit?: () => void;
	onDelete?: (category: CategoryDto) => void;
	onClose?: () => void;
}

export const CategoryDetailView = ({ category, isLoading, onEdit, onDelete, onClose }: CategoryDetailViewProps) => {
	const navigate = useNavigate();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const canEditCategory = useAccess(AzureADRoles.Admin);

	const handleWorkOrderSelected = (workOrder: ListWorkOrderDto): void => {
		navigate(`/workorders/${workOrder.WorkOrderId}`);
	};

	const deleteCategory = () => {
		if (!category) return;

		if (onDelete && category)
			onDelete(category);
	}

	if (isLoading) return <Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 160px)' }}>
				<Center>
					<Loader size="sm" mt="lg" />
				</Center>
			</ScrollArea>
		</Paper>
	if (!category) return <Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 160px)' }}>
				<Space h="md" />
				<Stack justify="center" align="center" gap="md" pt="lg">
					<ThemeIcon size={100} radius={100} variant="light" color="gray">
						<IconTag size={60} />
					</ThemeIcon>
					<Text fz="lg" fw="500" c="dimmed">Select a category to view details</Text>
				</Stack>
				<Space h="md" />
			</ScrollArea>
		</Paper>

	return <>
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 160px)' }}>
				<Stack gap={0} mb="md" justify="center" className={classes['header']}>
					<Group justify="space-between" px="20" wrap="nowrap">
						<Group gap="xs" wrap="nowrap">
							<ActionIcon variant="light" radius="xl" onClick={onClose} hiddenFrom="sm">
									<IconChevronLeft />
							</ActionIcon>
							<Stack gap={0}>
								<Title order={isMobile ? 6 : 3}>{category?.Title}</Title>
								<Text fz="xs" c="dimmed">Created on {moment(category?.Created).format("MMMM D, YYYY")}</Text>
							</Stack>
						</Group>
						<Group gap="xs">
							<Button.Group mr={-10}>
								{category.Deleted == null && <>
									{!isMobile &&
									<Button onClick={onEdit} disabled={!canEditCategory} variant="subtle" radius="xl" leftSection={<IconPencil stroke={1.5} />}>Edit</Button>
									}
									{isMobile &&
									<Button onClick={onEdit} disabled={!canEditCategory} variant="subtle" radius="xl" px="sm"><IconPencil stroke={1.5} /></Button>
									}
									<Divider orientation="vertical" />
									<Menu position="bottom-end" shadow="md" radius="lg">
										<Menu.Target>
											<Button disabled={!canEditCategory} variant="subtle" radius="xl" px="sm"><IconDots size={20} /></Button>
										</Menu.Target>
										<Menu.Dropdown>
											<Menu.Item onClick={deleteCategory} color="red" leftSection={<IconTrash stroke={1.5} />} fw="bold">Delete</Menu.Item>
										</Menu.Dropdown>
									</Menu>
								</>}
							</Button.Group>
						</Group>
					</Group>
				</Stack>

				{/* <Space h="md" />
				<Group justify="space-between" px="20">
					<Title order={3}>{category?.Title}</Title>
					<Button variant="outline" radius="md" leftSection={<IconPencil />} onClick={onEdit}>Edit</Button>
				</Group>
				<Space h="md" />
				<Divider mb="md" /> */}

				<Container px="20">
					{/* <Space h="md" /> */}
					{category?.Description && <>
						<Stack gap="xs">
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Description</Text>
							<Text fz="sm">{category?.Description}</Text>
						</Stack>
						<Divider my="md" />
					</>}
					
					<SimpleGrid cols={isMobile ? 1 : 2}>
						<CrudUserDate label="Created" userId={category.CreatedBy.UserId} userName={category.CreatedBy.Name} date={category.Created} />
						{category.UpdatedBy && !category.Deleted && <>
							<CrudUserDate label="Updated" userId={category.UpdatedBy.UserId} userName={category.UpdatedBy.Name} date={category.Updated} />
						</>}
						{category.DeletedBy && <>
							<CrudUserDate label="Deleted" userId={category.DeletedBy.UserId} userName={category.DeletedBy.Name} date={category.Deleted} />
						</>}
					</SimpleGrid>
					<Divider my="md" />

					<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Work Order History</Text>
					<Space h="md" />
					<CategoryWorkOrderList categoryId={category?.CategoryId} onSelect={handleWorkOrderSelected} />
					<Space h="md" />
				</Container>
			</ScrollArea>
		</Paper>
	</>;
};
